import React, { useEffect, useState } from 'react';
import { formatCompactCurrency } from '../utils/formatters';
import api from '../services/api';

interface ExistingBaseData {
  config: {
    companyName: string;
    currentEBITDA: number;
    fy26Target: number;
  };
  summary: {
    totalRevenue: number;
    avgGrowth: number;
    streamCount: number;
  };
  streams: Array<{
    id: string;
    name: string;
    annualRevenue: number;
    growthRate: number;
    renewalRate: number;
    category?: string;
    customerCount?: number;
    description?: string;
    isRevenue?: boolean; // true for revenue, false for costs
  }>;
}

export const ExistingBaseDashboard: React.FC = () => {
  const [data, setData] = useState<ExistingBaseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/dashboard/existing-base');
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load existing base data');
        }
      } catch (err) {
        setError('Failed to load existing base data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  // Separate revenue streams and costs
  const revenueStreams = data.streams.filter(s => s.isRevenue !== false && s.annualRevenue >= 0);
  const operatingCosts = data.streams.filter(s => s.isRevenue === false || s.annualRevenue < 0);

  const totalRevenue = revenueStreams.reduce((sum, s) => sum + Math.abs(s.annualRevenue), 0);
  const totalCosts = operatingCosts.reduce((sum, s) => sum + Math.abs(s.annualRevenue), 0);
  const netEBITDA = totalRevenue - totalCosts;

  return (
    <div className="bg-background text-text-primary">
      {/* Header */}
      <header className="bg-surface border-b-4 border-primary">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-primary mb-2">
              Existing Base Dashboard
            </h1>
            <p className="text-lg text-text-secondary">
              Recurring Revenue & Operating Costs - Foundation of £10.61M EBITDA
            </p>
            <p className="text-sm text-text-muted mt-2">
              Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Existing Base Overview */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Existing Base Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-background border-2 border-success rounded-lg p-6">
              <div className="text-sm font-medium text-primary uppercase tracking-wide mb-2">
                Total EBITDA Base
              </div>
              <div className="text-5xl font-bold text-success mb-2">
                {formatCompactCurrency(netEBITDA)}
              </div>
              <div className="text-xs text-text-muted uppercase tracking-wide">
                Oct 25 Starting Point
              </div>
            </div>
            <div className="bg-background border-2 border-border rounded-lg p-6">
              <div className="text-sm font-medium text-primary uppercase tracking-wide mb-2">
                Revenue Streams
              </div>
              <div className="text-5xl font-bold text-primary mb-2">
                {revenueStreams.length}
              </div>
              <div className="text-xs text-text-muted uppercase tracking-wide">
                Active Components
              </div>
            </div>
          </div>
        </section>

        {/* Revenue Streams */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Revenue Streams
          </h2>
          <div className="space-y-4">
            {revenueStreams.map((stream) => (
              <div
                key={stream.id}
                className="bg-background border-2 border-success rounded-lg p-6"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="text-lg font-semibold text-text-primary">{stream.name}</div>
                  <div className="text-3xl font-bold text-success">
                    {formatCompactCurrency(Math.abs(stream.annualRevenue))}
                  </div>
                </div>
                <div className="text-sm text-text-secondary">
                  {stream.description || stream.category || 'Recurring revenue stream'}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Operating Costs */}
        {operatingCosts.length > 0 && (
          <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
              Operating Costs
            </h2>
            <div className="space-y-4">
              {operatingCosts.map((cost) => (
                <div
                  key={cost.id}
                  className="bg-background border-2 border-danger rounded-lg p-6"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="text-lg font-semibold text-text-primary">{cost.name}</div>
                    <div className="text-3xl font-bold text-danger">
                      -{formatCompactCurrency(Math.abs(cost.annualRevenue))}
                    </div>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {cost.description || cost.category || 'Operating expense'}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Existing Base Summary */}
        <section className="bg-surface border-2 border-border rounded-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-primary mb-6 pb-4 border-b-2 border-primary">
            Existing Base Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-light border-b-2 border-primary">
                  <th className="text-left py-3 px-4 text-primary font-bold">Component</th>
                  <th className="text-left py-3 px-4 text-primary font-bold">Category</th>
                  <th className="text-right py-3 px-4 text-primary font-bold">Value</th>
                </tr>
              </thead>
              <tbody>
                {/* Revenue Streams */}
                {revenueStreams.map((stream) => (
                  <tr
                    key={stream.id}
                    className="border-b border-border hover:bg-surface-light transition-colors"
                  >
                    <td className="py-4 px-4">
                      <p className="font-medium text-text-primary">{stream.name}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-text-secondary">Revenue</p>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <p className="font-bold text-success">
                        {formatCompactCurrency(Math.abs(stream.annualRevenue))}
                      </p>
                    </td>
                  </tr>
                ))}

                {/* Total Revenue Row */}
                <tr className="bg-success bg-opacity-20 border-t-2 border-success font-bold">
                  <td className="py-4 px-4">
                    <p className="text-text-primary font-bold text-lg">TOTAL REVENUE</p>
                  </td>
                  <td className="py-4 px-4">
                    <p className="font-bold">Revenue</p>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <p className="text-success font-bold text-xl">
                      {formatCompactCurrency(totalRevenue)}
                    </p>
                  </td>
                </tr>

                {/* Operating Costs */}
                {operatingCosts.map((cost) => (
                  <tr
                    key={cost.id}
                    className="border-b border-border hover:bg-surface-light transition-colors"
                  >
                    <td className="py-4 px-4">
                      <p className="font-medium text-text-primary">{cost.name}</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-sm text-text-secondary">Operating Cost</p>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <p className="font-bold text-danger">
                        -{formatCompactCurrency(Math.abs(cost.annualRevenue))}
                      </p>
                    </td>
                  </tr>
                ))}

                {/* Total Costs Row */}
                {operatingCosts.length > 0 && (
                  <tr className="bg-danger bg-opacity-20 border-t-2 border-danger font-bold">
                    <td className="py-4 px-4">
                      <p className="text-text-primary font-bold text-lg">TOTAL COSTS</p>
                    </td>
                    <td className="py-4 px-4">
                      <p className="font-bold">Operating Cost</p>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <p className="text-danger font-bold text-xl">
                        -{formatCompactCurrency(totalCosts)}
                      </p>
                    </td>
                  </tr>
                )}

                {/* Net EBITDA Base Row */}
                <tr className="bg-surface-light border-t-4 border-primary font-bold">
                  <td className="py-5 px-4">
                    <p className="text-primary font-bold text-2xl">NET EBITDA BASE</p>
                  </td>
                  <td className="py-5 px-4">
                    <p className="font-bold text-lg">Total</p>
                  </td>
                  <td className="py-5 px-4 text-right">
                    <p className="text-primary font-bold text-3xl">
                      {formatCompactCurrency(netEBITDA)}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
};
